import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { matchPath, useLocation } from 'react-router';

import { TOP_BAR_HEIGHT } from '@/components/layout/topBar/TopBar.styles.ts';
import { announcementPreview } from '@/components/teamAnnouncements/teamAnnouncements.util.ts';
import { useMarkTeamAnnouncementsRead } from '@/components/teamAnnouncements/useMarkTeamAnnouncementsRead.util.ts';
import { useTeamAnnouncements } from '@/components/teamAnnouncements/useTeamAnnouncements.util.ts';
import { personFullName } from '@/components/ui/userAvatar/userAvatar.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { readTimeLabel } from '@/views/chats/utils/chatReadReceipts.util.ts';
import { polishPluralForm } from '@/views/processes/offer/components/aiChat/util/offerAiQuestion.util.ts';

const FEED_LIMIT = 5;

export const TeamAnnouncementsBar: React.FC = () => {
  const { t } = useTranslationWithPrefix('components.teamAnnouncements');
  const { pathname } = useLocation();
  const { push } = useAppNavigation();
  const { userId } = useUserContext();
  const { feed } = useTeamAnnouncements(FEED_LIMIT);
  const { isMarking, markRead } = useMarkTeamAnnouncementsRead();

  const isOnDashboard = matchPath({ end: true, path: RouteEnum.DASHBOARD }, pathname) !== null;
  const unread = feed?.items.filter(item => !item.isRead && item.sender.userId !== userId) ?? [];
  const newest = feed?.items[0];
  const shown = unread[0];

  if (isOnDashboard || !feed || !newest || !shown || feed.unreadCount === 0) {
    return null;
  }

  const moreCount = feed.unreadCount - 1;

  return (
    <Paper
      aria-live="polite"
      data-team-announcement-bar
      elevation={0}
      role="status"
      sx={{
        alignItems: 'center',
        background: themeColors.white,
        border: `1px solid ${themeColors.accentColor20}`,
        borderLeft: `4px solid ${themeColors.accentColor}`,
        borderRadius: 3,
        boxShadow: '0 8px 24px rgba(17, 17, 17, 0.12)',
        display: 'flex',
        gap: 1.5,
        left: '50%',
        maxWidth: 'calc(100vw - 32px)',
        position: 'fixed',
        px: 2,
        py: 1.25,
        top: `calc(${TOP_BAR_HEIGHT} + 8px)`,
        transform: 'translateX(-50%)',
        width: 720,
        zIndex: theme => theme.zIndex.appBar + 1,
      }}
    >
      <CampaignOutlinedIcon
        sx={{ color: themeColors.accentColorDark, flexShrink: 0, fontSize: 26 }}
      />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
          <Typography
            component="span"
            sx={{ color: themeColors.accentColorDark }}
            variant="f12Medium"
          >
            {t('label')}
          </Typography>
          {` · ${personFullName(shown.sender)} · ${readTimeLabel(shown.createdAt)}`}
        </Typography>
        <Typography
          sx={{
            color: themeColors.black,
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
          }}
          title={announcementPreview(shown.content)}
          variant="f14Medium"
        >
          {announcementPreview(shown.content)}
        </Typography>
        {moreCount > 0 && (
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {t(`more.${polishPluralForm(moreCount)}`, { count: moreCount })}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
        <Button
          onClick={() => push(RouteEnum.DASHBOARD)}
          size="small"
          sx={{ color: themeColors.greyDarker }}
          variant="text"
        >
          {t('showAll')}
        </Button>
        <Button
          loading={isMarking}
          onClick={() => void markRead({ lastReadAt: newest.createdAt })}
          size="small"
          sx={{
            '&:hover': { background: themeColors.accentColorDark },
            background: themeColors.accentColor,
            color: themeColors.white,
          }}
          variant="contained"
        >
          {t('markRead')}
        </Button>
      </Stack>
    </Paper>
  );
};
